import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { createSlug } from '../../../../util/urlHelpers';
import { Modal, NamedLink, ResponsiveImage, LinkedLogo } from '../../../../components';

import IconSearchDesktop from '../TopbarSearchForm/IconSearchDesktop';
import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import { searchModalListings, clearSearchModal } from '../../../../ducks/searchModal.duck';

import css from './SearchModal.module.css';

/**
 * SearchModal renders a fullscreen dark modal on desktop.
 * It provides an input to search listings instantly with auto-suggestions.
 */
const SearchModal = props => {
  const { isOpen, onClose, onManageDisableScrolling } = props;
  const [searchText, setSearchText] = useState('');

  const dispatch = useDispatch();
  const config = useConfiguration();
  const intl = useIntl();
  const debounceTimerRef = useRef(null);

  const { resultIds, totalResults, searchInProgress } = useSelector(
    state => state.searchModal || {}
  );
  const listings = useSelector(state => getListingsById(state, resultIds || []));

  // Clear search results on mount or when modal opens/closes
  useEffect(() => {
    dispatch(clearSearchModal());
    setSearchText('');
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isOpen, dispatch]);

  const handleInputChange = e => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.trim() === '') {
      dispatch(clearSearchModal());
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      dispatch(searchModalListings({ keywords: value, config }));
    }, 400);
  };

  const handleClose = () => {
    dispatch(clearSearchModal());
    setSearchText('');
    onClose();
  };

  return (
    <Modal
      id="DesktopSearchModal"
      isOpen={isOpen}
      onClose={handleClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      lightCloseButton
      containerClassName={css.modalContainer}
      scrollLayerClassName={css.modalScrollLayer}
    >
      <div className={css.searchModalContent}>
        <div className={css.logoWrapper}>
          <LinkedLogo layout="desktop" className={css.logo} onClick={handleClose} />
        </div>

        <div className={css.searchField}>
          <h1 className={css.searchTitle}>
            <FormattedMessage id="SearchModal.title" />
          </h1>

          <div className={css.searchBarWrapper}>
            <div className={css.searchIcon}>
              <IconSearchDesktop />
            </div>
            <input
              type="text"
              className={css.searchInput}
              placeholder={intl.formatMessage({ id: 'SearchModal.placeholder' })}
              value={searchText}
              onChange={handleInputChange}
              autoFocus
              autoComplete="off"
            />
            {searchInProgress && (
              <div className={css.spinnerWrapper}>
                <div className={css.spinner} />
              </div>
            )}
          </div>
        </div>

        {/* Results List */}
        {searchText.trim() !== '' && !searchInProgress && (
          <div className={css.resultsWrapper}>
            <p className={css.resultsCount}>
              <FormattedMessage
                id="SearchModal.resultsFound"
                values={{ count: totalResults || 0 }}
              />
            </p>

            <div className={css.resultsList}>
              {listings.map(listing => {
                const title = listing.attributes?.title || '';
                const slug = createSlug(title);
                const id = listing.id?.uuid;
                const firstImage = listing.images?.[0] || null;
                const publicData = listing.attributes?.publicData || {};
                const heroImageCloudinaryUrl = publicData?.heroImageCloudinaryUrl;
                const categoryText =
                  publicData?.screen_tag && typeof publicData.screen_tag === 'string'
                    ? publicData.screen_tag.toUpperCase()
                    : publicData?.categoryLevel1
                    ? publicData.categoryLevel1.toUpperCase()
                    : 'HERITAGE';
                const descriptionContent =
                  publicData?.subtitle || listing.attributes?.description || '';

                return (
                  <div key={id} className={css.resultItem}>
                    {/* Thumbnail Image on the Left */}
                    <NamedLink
                      name="ListingPage"
                      params={{ id, slug }}
                      className={css.imageLink}
                      onClick={handleClose}
                    >
                      <div className={css.imageContainer}>
                        <span className={css.categoryBadge}>{categoryText.toUpperCase()}</span>
                        {heroImageCloudinaryUrl ? (
                          <img src={heroImageCloudinaryUrl} alt={title} className={css.thumbnail} />
                        ) : firstImage ? (
                          <ResponsiveImage
                            rootClassName={css.thumbnail}
                            alt={title}
                            image={firstImage}
                            variants={['scaled-small', 'scaled-medium']}
                          />
                        ) : (
                          <div className={css.thumbnailPlaceholder} />
                        )}
                      </div>
                    </NamedLink>

                    {/* Listing Info on the Right */}
                    <div className={css.itemInfo}>
                      <div className={css.itemTextBlock}>
                        <NamedLink
                          name="ListingPage"
                          params={{ id, slug }}
                          className={css.titleLink}
                          onClick={handleClose}
                        >
                          <h2 className={css.itemTitle}>{title}</h2>
                        </NamedLink>
                        <p className={css.itemDescription}>{descriptionContent}</p>
                      </div>

                      <div className={css.itemActions}>
                        <NamedLink
                          name="ListingPage"
                          params={{ id, slug }}
                          className={css.actionLink}
                          onClick={handleClose}
                        >
                          <FormattedMessage id="SearchModal.discover" />
                        </NamedLink>
                        <NamedLink
                          name="BookingFlowPage"
                          params={{ id, slug }}
                          className={css.actionLink}
                          onClick={handleClose}
                        >
                          <FormattedMessage id="SearchModal.reserve" />
                        </NamedLink>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SearchModal;
